import { useState } from "react";
import { adminImport } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UploadCloud, FileJson, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

export default function AdminImport() {
  const [file, setFile] = useState(null);
  const [target, setTarget] = useState("content");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    setResult(null);
    try {
      const res = await adminImport(file, target);
      setResult(res);
      toast("Import complete", { description: `${res.inserted} of ${res.total} imported` });
    } catch (err) {
      toast.error("Import failed", { description: err.response?.data?.detail || err.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="font-serif-latin text-3xl text-ink">Bulk Import</h1>
      <p className="mt-1 text-sm text-ink-3">Upload a JSON array or a CSV file to add many entries at once.</p>
      <div className="mt-3 flex flex-wrap gap-3">
        <a href="/sample-import.csv" download data-testid="sample-csv" className="inline-flex items-center gap-2 border border-line px-3 py-1.5 text-xs text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink"><FileSpreadsheet className="h-3.5 w-3.5 text-gold" /> Download sample CSV</a>
        <a href="/sample-import.json" download data-testid="sample-json" className="inline-flex items-center gap-2 border border-line px-3 py-1.5 text-xs text-ink-2 transition-colors hover:border-[var(--ink)] hover:text-ink"><FileJson className="h-3.5 w-3.5 text-gold" /> Download sample JSON</a>
      </div>

      <form onSubmit={submit} className="mt-8 space-y-5 border border-line bg-surface p-6 md:p-8">
        <div>
          <label className="mb-1.5 block text-sm text-ink-2">Import into</label>
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger data-testid="import-target" className="rounded-none border-line bg-parchment"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none border-line bg-surface">
              <SelectItem value="content">Knowledge Content</SelectItem>
              <SelectItem value="services">Services (Pooja / Yagya)</SelectItem>
              <SelectItem value="hub">Knowledge Hub</SelectItem>
              <SelectItem value="deities">Deities</SelectItem>
              <SelectItem value="temples">Temples</SelectItem>
              <SelectItem value="quiz">Dharma Quiz</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <label className="flex cursor-pointer flex-col items-center justify-center gap-3 border-2 border-dashed border-line bg-parchment px-6 py-12 text-center transition-colors hover:border-[var(--gold-muted)]">
          <UploadCloud className="h-8 w-8 text-saffron" />
          <span className="text-sm text-ink-2">{file ? file.name : "Click to choose a .json or .csv file"}</span>
          <input data-testid="import-file" type="file" accept=".json,.csv" className="hidden" onChange={(e) => setFile(e.target.files[0])} />
        </label>

        <button data-testid="import-submit" disabled={busy || !file} className="w-full bg-[var(--saffron)] py-3 font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-50">
          {busy ? "Importing…" : "Import"}
        </button>
      </form>

      {result && (
        <div data-testid="import-result" className="mt-6 border border-line bg-[var(--surface-alt)]/50 p-6">
          <p className="text-ink">Imported <span className="font-medium text-saffron">{result.inserted}</span> of {result.total} rows.</p>
          {result.errors?.length > 0 && (
            <div className="mt-3">
              <p className="text-sm text-[var(--saffron-dark)]">{result.errors.length} row(s) had errors:</p>
              <ul className="mt-1 max-h-40 overflow-y-auto text-xs text-ink-3">
                {result.errors.map((er, i) => <li key={i}>Row {er.row}: {er.error}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="mt-10 border border-line bg-surface p-6 text-sm text-ink-2">
        <p className="mb-3 flex items-center gap-2 font-medium text-ink"><FileJson className="h-4 w-4 text-gold" /> JSON format</p>
        <p className="text-ink-3">An array of objects matching the content/service fields (e.g. <code className="text-xs">type, devanagari_title, title, description, body, sanskrit_text, tags</code>). Nested language fields use objects like <code className="text-xs">{`"meaning": {"en": "...", "hi": "..."}`}</code>.</p>
        <p className="mb-3 mt-5 flex items-center gap-2 font-medium text-ink"><FileSpreadsheet className="h-4 w-4 text-gold" /> CSV format</p>
        <p className="text-ink-3">Header row of field names. Language fields use columns like <code className="text-xs">meaning_en</code>, <code className="text-xs">meaning_hi</code>. List fields (tags, materials, includes) are pipe-separated, e.g. <code className="text-xs">shiva|vedic</code>.</p>
      </div>
    </div>
  );
}
